import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import rackCategoryServices from './rackCategoryServices';
import type {ADD_RACK_CATEGORY, UPDATE_RACK_CATEGORY, RACK_CATEGORY} from './rackCategory.type';
import type FILTERS from '../../../../common/filters.type';

const initialState = {
  isLoading: false,
  rackCategories: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as undefined | boolean,
    pageData: [] as RACK_CATEGORY[],
  },
};

export const rackCategoryList = createAsyncThunk(
  'rackCategoryList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await rackCategoryServices.rackCategoriesList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addRackCategory = createAsyncThunk(
  'addRackCategory',
  async (values: ADD_RACK_CATEGORY, thunkAPI) => {
    try {
      const data: AxiosResponse = await rackCategoryServices.addRackCategory(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const updateRackCategory = createAsyncThunk(
  'updateRackCategory',
  async (
    values: {
      payload: UPDATE_RACK_CATEGORY;
      category_id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await rackCategoryServices.updateRackCategory({
        payload: values.payload,
        category_id: values.category_id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const rackCategorySlice = createSlice({
  name: 'rackCategories',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.rackCategories.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.rackCategories.page_number = 1;
      state.rackCategories.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.rackCategories.page_number = 1;
      state.rackCategories.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.rackCategories.ascending = action.payload.ascending;
      state.rackCategories.sort_by = action.payload.sortBy;
    },
    rackCategoryStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.rackCategories.page_number = 1;
      state.rackCategories.is_active = action.payload;
    },
    clearRackCategoryFilters(state) {
      state.rackCategories.page_size = 10;
      state.rackCategories.page_number = 1;
      state.rackCategories.search = '';
      state.rackCategories.sort_by = 'name';
      state.rackCategories.is_active = undefined;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(rackCategoryList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(rackCategoryList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.rackCategories = action.payload.data;
    });
    builder.addCase(rackCategoryList.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(addRackCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addRackCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Rack Category Added Successfully`);
    });
    builder.addCase(addRackCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(updateRackCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateRackCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Rack Category updated Successfully`);
    });
    builder.addCase(updateRackCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  clearRackCategoryFilters,
  rackCategoryStatusFilter,
} = rackCategorySlice.actions;

export default rackCategorySlice.reducer;
